import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser, setCurrentUser } from '../../ducks/user.duck';
import { changeEmail, recoverPasswordOnServer } from '../../util/api';

// ================ Async thunks ================ //

export const resetPasswordThunk = createAsyncThunk(
  'ContactDetailsPage/resetPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      await recoverPasswordOnServer({ email });
      return true;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
export const resetPassword = email => dispatch => {
  return dispatch(resetPasswordThunk({ email }));
};

export const savePhoneNumberThunk = createAsyncThunk(
  'ContactDetailsPage/savePhoneNumber',
  ({ phoneNumber }, { dispatch, extra: sdk, rejectWithValue }) => {
    return sdk.currentUser
      .updateProfile(
        { protectedData: { phoneNumber } },
        {
          expand: true,
          include: ['profileImage'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
        }
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(setCurrentUser(entities[0]));
        return entities[0];
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
export const savePhoneNumber = params => dispatch => {
  return dispatch(savePhoneNumberThunk({ phoneNumber: params.phoneNumber }));
};

export const saveEmailThunk = createAsyncThunk(
  'ContactDetailsPage/requestSaveEmail',
  async ({ email, currentPassword }, { dispatch, rejectWithValue }) => {
    try {
      // Email change in this stack is a two-phase flow: this call asks
      // Supabase to send a confirmation email to the new address. The
      // user must click the link before the new email is live; that
      // verification lands at /verify-email?type=email_change which
      // then syncs the email through to Sharetribe via the IdP token.
      await changeEmail({ email, currentPassword });
      // Refresh currentUser so any UI that depends on `pendingEmail`
      // type semantics can update. The actual email field on Sharetribe
      // doesn't change until the new address is confirmed.
      dispatch(fetchCurrentUser({ enforce: true }));
      return { email, pending: true };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
export const saveEmail = params => dispatch => {
  return dispatch(
    saveEmailThunk({ email: params.email, currentPassword: params.currentPassword })
  );
};

export const saveEmailAndPhoneNumberThunk = createAsyncThunk(
  'ContactDetailsPage/saveEmailAndPhoneNumber',
  async ({ email, phoneNumber, currentPassword }, { dispatch, rejectWithValue }) => {
    const emailAction = await dispatch(saveEmailThunk({ email, currentPassword }));
    if (emailAction.meta?.requestStatus === 'rejected') {
      return rejectWithValue(emailAction.payload);
    }
    const phoneAction = await dispatch(savePhoneNumberThunk({ phoneNumber }));
    if (phoneAction.meta?.requestStatus === 'rejected') {
      return rejectWithValue(phoneAction.payload);
    }
    return phoneAction.payload;
  }
);
export const saveEmailAndPhoneNumber = params => dispatch => {
  return dispatch(
    saveEmailAndPhoneNumberThunk({
      email: params.email,
      currentPassword: params.currentPassword,
      phoneNumber: params.phoneNumber,
    })
  );
};

export const saveContactDetailsThunk = createAsyncThunk(
  'ContactDetailsPage/saveContactDetails',
  async (
    { email, currentEmail, phoneNumber, currentPhoneNumber, currentPassword },
    { dispatch, rejectWithValue }
  ) => {
    const emailChanged = email !== currentEmail;
    const phoneNumberChanged = phoneNumber !== currentPhoneNumber;

    let action;
    if (emailChanged && phoneNumberChanged) {
      action = await dispatch(
        saveEmailAndPhoneNumberThunk({ email, currentPassword, phoneNumber })
      );
    } else if (emailChanged) {
      action = await dispatch(saveEmailThunk({ email, currentPassword }));
    } else if (phoneNumberChanged) {
      action = await dispatch(savePhoneNumberThunk({ phoneNumber }));
    } else {
      return null;
    }

    if (action.meta?.requestStatus === 'rejected') {
      return rejectWithValue(action.payload);
    }
    return action.payload;
  }
);
export const saveContactDetails = params => dispatch => {
  return dispatch(saveContactDetailsThunk(params));
};

// ================ Slice ================ //

const contactDetailsSlice = createSlice({
  name: 'ContactDetailsPage',
  initialState: {
    saveEmailError: null,
    savePhoneNumberError: null,
    saveContactDetailsInProgress: false,
    contactDetailsChanged: false,
    pendingEmail: null,
    resetPasswordInProgress: false,
    resetPasswordError: null,
  },
  reducers: {
    saveContactDetailsClear: state => {
      state.saveContactDetailsInProgress = false;
      state.saveEmailError = null;
      state.savePhoneNumberError = null;
      state.contactDetailsChanged = false;
      state.pendingEmail = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetPasswordThunk.pending, state => {
        state.resetPasswordInProgress = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPasswordThunk.fulfilled, state => {
        state.resetPasswordInProgress = false;
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.resetPasswordInProgress = false;
        state.resetPasswordError = action.payload;
      })
      .addCase(savePhoneNumberThunk.rejected, (state, action) => {
        state.saveContactDetailsInProgress = false;
        state.savePhoneNumberError = action.payload;
      })
      .addCase(saveEmailThunk.fulfilled, (state, action) => {
        state.pendingEmail = action.payload?.email || null;
      })
      .addCase(saveEmailThunk.rejected, (state, action) => {
        state.saveContactDetailsInProgress = false;
        state.saveEmailError = action.payload;
      })
      .addCase(saveContactDetailsThunk.pending, state => {
        state.saveContactDetailsInProgress = true;
        state.saveEmailError = null;
        state.savePhoneNumberError = null;
        state.contactDetailsChanged = false;
        state.pendingEmail = null;
      })
      .addCase(saveContactDetailsThunk.fulfilled, state => {
        state.saveContactDetailsInProgress = false;
        state.contactDetailsChanged = true;
      })
      .addCase(saveContactDetailsThunk.rejected, state => {
        state.saveContactDetailsInProgress = false;
      });
  },
});

export const { saveContactDetailsClear } = contactDetailsSlice.actions;
export default contactDetailsSlice.reducer;

// ================ Load data ================ //

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};
